import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { SupportDraftEditor, type DraftEditorCopy } from "@/components/admin/SupportDraftEditor";
import { runSupportReview } from "@/lib/support-review.functions";
import {
  listSupport,
  readSupport,
  saveSupportDraft,
  supportAction,
  sendSupportDraft,
  calculateSupportQuote,
  archiveSupportAttachments,
  downloadSupportAttachment,
} from "@/lib/support.functions";

export const Route = createFileRoute("/admin/support")({
  head: () => ({
    meta: [{ title: "Support | Event Sverige" }, { name: "robots", content: "noindex, nofollow" }],
  }),
  validateSearch: (search: Record<string, unknown>) => ({
    ticket: typeof search.ticket === "string" ? search.ticket : undefined,
  }),
  component: Support,
});

type SupportStatus = "new" | "in_progress" | "waiting" | "resolved" | "archived";

function Support() {
  const list = useServerFn(listSupport);
  const runReview = useServerFn(runSupportReview);
  const query = useQuery({ queryKey: ["support"], queryFn: () => list({}) });
  const qc = useQueryClient();
  const [ticketId, setTicketId] = useState(Route.useSearch().ticket ?? "");
  const [busy, setBusy] = useState(false);
  if (query.isPending) return <p>Läser support…</p>;
  if (query.error) return <p role="alert">{query.error.message} — se teknisk aktiveringsplan.</p>;
  const data = query.data;
  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold">Support och godkännandekö</h1>
        <p className="mt-2 text-sm">
          Alla kundsvar granskas.{" "}
          {data.sendingEnabled
            ? "Sändning är aktiverad efter granskning."
            : "Kundsändning är avstängd."}
        </p>
        <button
          className="mt-3 rounded-lg border px-3 py-2 text-sm"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              const result = await runReview({});
              if (result.blocked) {
                toast.error("Hämtning eller utkast kräver kontroll. Mejlet ligger kvar i kön.");
              } else {
                toast.success(`Granskningskö: ${result.fetched} hämtade original.`);
              }
              await qc.invalidateQueries({ queryKey: ["support"] });
            } catch (error) {
              toast.error(
                error instanceof Error ? error.message : "Granskningsflödet kunde inte köras",
              );
            } finally {
              setBusy(false);
            }
          }}
        >
          Hämta nästa mejl till privat granskning
        </button>
      </header>
      <section className="rounded-xl border p-4">
        <h2 className="font-bold">Verifierad inkorg ({data.inbox.length})</h2>
        <p className="text-sm">
          Mejl lagras innan kvittering. Knappen ovan lagrar originalet privat. Svarsförslag granskas
          i Joels chatt; inget svar skickas härifrån.
        </p>
        {data.inbox.map((i) => (
          <div key={i.emailId} className="flex flex-wrap items-center gap-3 py-2">
            <span>
              {new Date(i.receivedAt).toLocaleString("sv-SE")} · {i.from} · {i.subject} ·{" "}
              {i.fetchStatus}
            </span>
            {i.lastError && <span role="alert">{i.lastError}</span>}
          </div>
        ))}
      </section>
      <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
        <nav aria-label="Supportärenden" className="space-y-2">
          {data.tickets.map((t) => (
            <button
              key={t.id}
              onClick={() => setTicketId(t.id)}
              className={`w-full rounded-xl border p-3 text-left ${ticketId === t.id ? "bg-primary/10" : ""}`}
            >
              <b className="block break-words">{t.subject}</b>
              <span className="text-sm break-all">
                {t.customer_email} · {t.status}
              </span>
            </button>
          ))}
        </nav>
        {ticketId ? (
          <Ticket key={ticketId} ticketId={ticketId} sendingEnabled={data.sendingEnabled} />
        ) : (
          <p>Välj ett ärende för trådhistorik, offertunderlag och utkast.</p>
        )}
      </div>
      <section className="rounded-xl border p-4">
        <h2 className="font-bold">Versionsstyrd kunskap</h2>
        <p className="text-sm">
          Reviderad broschyr/priser förblir opublicerade. Godkännande sker i separat
          kunskapsgranskning.
        </p>
        {data.knowledge.map((k) => (
          <details key={k.id}>
            <summary>
              {k.version} · {k.status}
            </summary>
            <pre className="whitespace-pre-wrap break-words text-sm">
              {JSON.stringify(k.content, null, 2)}
            </pre>
          </details>
        ))}
      </section>
    </div>
  );
}
function Ticket({ ticketId, sendingEnabled }: { ticketId: string; sendingEnabled: boolean }) {
  const read = useServerFn(readSupport);
  const save = useServerFn(saveSupportDraft);
  const action = useServerFn(supportAction);
  const send = useServerFn(sendSupportDraft);
  const archive = useServerFn(archiveSupportAttachments);
  const download = useServerFn(downloadSupportAttachment);
  const query = useQuery({
    queryKey: ["support", ticketId],
    queryFn: () => read({ data: { ticketId } }),
  });
  const qc = useQueryClient();
  const [editorCopy, setEditorCopy] = useState<DraftEditorCopy | null>(null);
  const [resolution, setResolution] = useState("");
  const [evidence, setEvidence] = useState("");
  const [busy, setBusy] = useState(false);
  async function run(task: () => Promise<unknown>) {
    setBusy(true);
    try {
      await task();
      await qc.invalidateQueries({ queryKey: ["support"] });
      toast.success("Sparat i supportloggen");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Åtgärden misslyckades");
    } finally {
      setBusy(false);
    }
  }
  if (query.isPending) return <p>Läser ärendet…</p>;
  if (query.error) return <p role="alert">{query.error.message}</p>;
  const data = query.data;
  const replyBlocked = Boolean(
    data.messages.filter((m) => m.direction === "inbound").at(-1)?.blockers?.length,
  );

  return (
    <main className="min-w-0 space-y-5">
      <h2 className="text-xl font-bold">{data.ticket.subject}</h2>
      <label>
        Status{" "}
        <select
          className="rounded border p-2"
          disabled={busy}
          value={data.ticket.status}
          onChange={(e) =>
            run(() =>
              action({
                data: {
                  ticketId,
                  action: "status",
                  status: e.target.value as SupportStatus,
                },
              }),
            )
          }
        >
          {["new", "in_progress", "waiting", "resolved", "archived"].map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
      </label>
      {data.messages.map((m) => (
        <article className="rounded-xl border p-4" key={m.id}>
          <p className="font-bold">
            {m.direction === "inbound" ? "Inkommande" : "Skickat"} ·{" "}
            {new Date(m.created_at).toLocaleString("sv-SE")}
          </p>
          <pre className="mt-2 whitespace-pre-wrap break-words font-sans">{m.body}</pre>
          {(m.blockers as string[]).map((b) => (
            <p className="mt-2 text-red-700" key={b}>
              Spärr: {b}
            </p>
          ))}
          {m.metadata?.attachments?.length > 0 && (
            <>
              <p className="mt-2 text-sm">
                Bilagor: {m.metadata.attachments.map((a) => a.filename || "bilaga").join(", ")}
              </p>
              <button
                disabled={busy}
                className="underline"
                onClick={() => run(() => archive({ data: { messageId: m.id } }))}
              >
                Arkivera bilagor privat i karantän
              </button>
            </>
          )}
        </article>
      ))}
      {data.attachments.map((a) => (
        <div key={a.id} className="rounded border p-3">
          {a.filename} · {a.byte_size} byte · {a.status}
          {a.status === "clean" && (
            <button
              className="ml-3 underline"
              onClick={() =>
                run(async () => {
                  const r = await download({ data: { attachmentId: a.id } });
                  window.open(r.url, "_blank", "noopener,noreferrer");
                })
              }
            >
              Hämta privat bilaga
            </button>
          )}
        </div>
      ))}
      {data.ticket.source === "web" && (
        <section className="rounded border p-4">
          <label className="block">
            Oberoende verifiering av webbavsändare
            <input
              className="mt-2 w-full rounded border p-2"
              value={evidence}
              onChange={(e) => setEvidence(e.target.value)}
              placeholder="Dokumentera exempelvis kontrollerat telefonsamtal"
            />
          </label>
          <button
            disabled={busy || evidence.trim().length < 10}
            className="mt-2 underline"
            onClick={() =>
              run(() => action({ data: { ticketId, action: "verify_web", evidence } }))
            }
          >
            Registrera verifiering
          </button>
        </section>
      )}
      <section className="rounded-xl border p-4 space-y-3">
        <h3 className="font-bold">Svarsutkast</h3>
        <SupportDraftEditor
          generation={data.ticket.context_generation}
          busy={busy}
          blocked={replyBlocked}
          copy={editorCopy}
          onSave={(draft) => run(() => save({ data: { ticketId, ...draft } }))}
        />
        <p className="text-sm">
          Utkast tas fram och granskas i Joels chatt. Spärrar för spoofing, no-reply, massutskick
          och robotmejl gäller även här.
        </p>
      </section>
      <label className="block">
        Joels granskningsanteckning / lösta eskaleringar
        <textarea
          rows={3}
          className="mt-2 w-full rounded border p-2"
          value={resolution}
          onChange={(e) => setResolution(e.target.value)}
        />
      </label>
      {data.drafts.map((d) => (
        <article key={d.id} className="space-y-3 rounded-xl border p-4">
          <h3 className="font-bold">
            Utkast · {d.status} · revision {d.revision}
          </h3>
          <pre className="whitespace-pre-wrap break-words font-sans">{d.body}</pre>
          <p className="text-sm">
            Kunskap: {d.knowledge_id || "Manuellt"} · Källor: {(d.sources as string[]).join(", ")}
          </p>
          {(d.adaptations as string[]).map((a) => (
            <p key={a}>Förslag: {a}</p>
          ))}
          {(d.escalations as string[]).map((a) => (
            <p key={a} className="text-amber-800">
              Joel: {a}
            </p>
          ))}
          <div className="flex gap-4 flex-wrap">
            {d.status === "pending" && (
              <button
                disabled={busy || replyBlocked}
                title={replyBlocked ? "Hämta och verifiera inkommande innehåll först" : undefined}
                className="underline"
                onClick={() =>
                  run(() =>
                    action({
                      data: {
                        ticketId,
                        action: "approve",
                        draftId: d.id,
                        revision: d.revision,
                        contextGeneration: d.context_generation,
                        resolution,
                      },
                    }),
                  )
                }
              >
                Godkänn exakt detta utkast
              </button>
            )}
            {["pending", "approved"].includes(d.status) && (
              <>
                <button
                  disabled={busy}
                  className="underline"
                  onClick={() =>
                    setEditorCopy({ body: d.body, contextGeneration: d.context_generation })
                  }
                >
                  Bearbeta som ny version
                </button>
                <button
                  disabled={busy}
                  className="underline"
                  onClick={() =>
                    run(() => action({ data: { ticketId, action: "reject", draftId: d.id } }))
                  }
                >
                  Avvisa
                </button>
              </>
            )}
            {d.status === "approved" && (
              <button
                disabled={busy || !sendingEnabled || replyBlocked}
                className="underline"
                onClick={() => run(() => send({ data: { ticketId, draftId: d.id, resolution } }))}
              >
                Skicka godkänt svar till {data.ticket.customer_email}
              </button>
            )}
            {["sending", "uncertain"].includes(d.status) && (
              <p role="alert">
                Kontrollera Resend-loggen. Ingen ny sändning är tillåten från detta utkast.
              </p>
            )}
          </div>
        </article>
      ))}
      <Quote />
      <details>
        <summary>Åtgärdslogg</summary>
        {data.audit.map((a) => (
          <p key={a.id} className="text-sm">
            {new Date(a.created_at).toLocaleString("sv-SE")} · {a.action} ·{" "}
            {a.action.startsWith("automation_") ? "Automatik" : a.actor || "Webb"}
          </p>
        ))}
      </details>
    </main>
  );
}
function Quote() {
  const calculate = useServerFn(calculateSupportQuote);
  const [values, setValues] = useState({
    people: 1,
    eventHours: 1,
    rigHours: 1,
    actualMiles: 0,
    rounding: "half" as "half" | "whole",
  });
  const [result, setResult] = useState<Awaited<ReturnType<typeof quoteResult>> | null>(null);
  async function quoteResult() {
    return calculate({ data: values });
  }
  return (
    <section className="rounded-xl border p-4 space-y-3">
      <h3 className="font-bold">Offertunderlag</h3>
      <p className="text-sm">
        650 kr/person/timme även rigg, minst 1 timme montering. 160 kr/mil faktisk resa.
        Stämningsljus ingår i varje fysiskt eventpaket.
      </p>
      <div className="grid grid-cols-2 gap-3">
        {(
          [
            ["people", "Personal"],
            ["eventHours", "Eventtimmar"],
            ["rigHours", "Monteringstimmar"],
            ["actualMiles", "Faktisk totalresa (mil)"],
          ] as const
        ).map(([key, label]) => (
          <label key={key}>
            {label}
            <input
              type="number"
              min={0}
              step={key === "people" ? 1 : 0.1}
              className="block w-full rounded border p-2"
              value={values[key]}
              onChange={(e) => setValues({ ...values, [key]: Number(e.target.value) })}
            />
          </label>
        ))}
      </div>
      <label>
        Avrundning
        <select
          value={values.rounding}
          onChange={(e) => setValues({ ...values, rounding: e.target.value as "half" | "whole" })}
        >
          <option value="half">Halvtimme uppåt</option>
          <option value="whole">Heltimme uppåt</option>
        </select>
      </label>
      <button
        className="ml-4 underline"
        onClick={async () => {
          try {
            setResult(await quoteResult());
          } catch (e) {
            toast.error(e instanceof Error ? e.message : "Kunde inte beräkna");
          }
        }}
      >
        Beräkna
      </button>
      {result && (
        <p>
          {result.hours} timmar · Personal {result.labour} kr · Resa {result.travel} kr · Summa
          underlag {result.subtotal} kr. {result.note}
        </p>
      )}
    </section>
  );
}
